import { useEffect, useState } from "react";
import {
  MagnifyingGlass,
  Phone,
  ChatCircle,
  EnvelopeSimple,
  NavigationArrow,
  CaretDown,
  User,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function MobileAccounts() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      api
        .get("/customers", { params: q ? { q } : {} })
        .then(({ data }) => setRows(data))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const action =
    "flex h-11 items-center justify-center gap-1.5 rounded-xl border border-zinc-700 text-xs font-semibold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:bg-zinc-800";

  return (
    <div className="px-4 pt-4" data-testid="mobile-accounts">
      <h1 className="m-title m-display text-3xl text-[#c39e6d]">Accounts</h1>

      <div className="relative mt-3">
        <MagnifyingGlass
          size={16}
          weight="bold"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
        />
        <input
          data-testid="mobile-account-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, company, phone…"
          className="h-12 w-full rounded-xl border border-zinc-800 bg-[#0F172A] pl-10 pr-3 text-base text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none"
        />
      </div>

      <div className="mt-4 space-y-2">
        {rows.length === 0 && (
          <div
            data-testid="mobile-accounts-empty"
            className="rounded-2xl border border-dashed border-zinc-800 p-8 text-center text-sm text-zinc-500"
          >
            No accounts found.
          </div>
        )}
        {rows.map((c) => {
          const open = openId === c.id;
          const addr = c.job_addresses?.[0] || c.main_address || c.address;
          return (
            <div
              key={c.id}
              data-testid={`mobile-account-${c.id}`}
              className="rounded-2xl border border-zinc-800 bg-[#0F172A]"
            >
              <button
                onClick={() => setOpenId(open ? "" : c.id)}
                className="flex w-full items-center gap-3 p-4 text-left"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#c39e6d]/15 text-xs font-bold uppercase text-[#c39e6d]">
                  {(c.name || "?").slice(0, 2)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-zinc-100">
                    {c.name}
                  </span>
                  <span className="block truncate text-xs text-zinc-500">
                    {c.company || c.group}
                  </span>
                </span>
                <CaretDown
                  size={16}
                  weight="bold"
                  className={`shrink-0 text-zinc-500 transition-transform duration-150 ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </button>
              {open && (
                <div
                  className="border-t border-zinc-800 p-4"
                  data-testid={`mobile-account-detail-${c.id}`}
                >
                  {addr && <div className="text-xs text-zinc-400">{addr}</div>}
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {c.phone && (
                      <a
                        href={`tel:${c.phone}`}
                        data-testid={`mobile-account-call-${c.id}`}
                        className={action}
                      >
                        <Phone size={15} weight="bold" />
                        Call
                      </a>
                    )}
                    {c.phone && (
                      <a
                        href={`sms:${c.phone}`}
                        data-testid={`mobile-account-text-${c.id}`}
                        className={action}
                      >
                        <ChatCircle size={15} weight="bold" />
                        Text
                      </a>
                    )}
                    {c.email && (
                      <a
                        href={`mailto:${c.email}`}
                        data-testid={`mobile-account-email-${c.id}`}
                        className={action}
                      >
                        <EnvelopeSimple size={15} weight="bold" />
                        Email
                      </a>
                    )}
                    {addr && (
                      <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(addr)}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`mobile-account-map-${c.id}`}
                        className={action}
                      >
                        <NavigationArrow size={15} weight="bold" />
                        Map
                      </a>
                    )}
                  </div>
                  {(c.contacts || []).length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-600">
                        Contacts
                      </div>
                      {c.contacts.map((ct, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs text-zinc-300"
                        >
                          <span className="font-semibold">{ct.name}</span>
                          {ct.phone && (
                            <a
                              href={`tel:${ct.phone}`}
                              className="text-[#c39e6d]"
                            >
                              {ct.phone}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
